import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatReadableAmount } from "../../../Common/FormatFunctions/FormatAmount";
import { RingLoader } from 'react-spinners';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper, TableFooter } from '@mui/material';
import { FormaDateBalanceSheet } from "../../../Common/FormatFunctions/FormatDate"

const apikey = process.env.REACT_APP_API;

const PurchaseTDSRegister = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // const { fromDate, toDate } = location.state || { fromDate: '', toDate: '' ,companyCode : '',Year_Code : ''};
    const searchParams = new URLSearchParams(location.search);
    const Company_Name = sessionStorage.getItem('Company_Name')
    const Company_GSTNO = sessionStorage.getItem('Company_GSTNO')
    const fromDate = searchParams.get('fromDate');
    const toDate = searchParams.get('toDate');
    const company_Code = searchParams.get('companyCode');
    const YearCode = searchParams.get('yearCode');
    const acCode = searchParams.get('acCode');

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    let [grandTotals, setGrandTotals] = useState({
        TotalTaxable_Amt: 0,
        CGSTAmt: 0,
        SGSTAmt: 0,
        IGSTAmt: 0,
        BillamountAmt: 0,
        TDSAmt: 0
    });

    const API_URL = `${apikey}/PurchaseTDS_Register`;

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear());
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {

        const fetchReportData = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await axios.get(API_URL, {
                    params: {
                        from_date: fromDate,
                        toDate: toDate,
                        companyCode: company_Code,
                        YearCode: YearCode,
                        acCode: acCode
                    },
                });
                setReportData(response.data);
            } catch (error) {
                console.error('Error fetching report:', error);
                setError('Error fetching report');
            } finally {
                setLoading(false);
            }
        };

        fetchReportData();
    }, [API_URL]);

    const handleExportToExcel = () => {
        const wb = XLSX.utils.book_new();
        const headers = [
            "PAN","Party Code", "Party Name", "Taxable Amount", "CGST", "SGST", "IGST", "Bill Amount", "TDS Amount"
        ];

        const partyTotals = {};

        reportData.forEach(item => {
            const party = item.Name_Of_Party;
            if (!partyTotals[party]) {
                partyTotals[party] = {
                    PAN: item.Pan,
                    "Party Code": item.Party_Code,
                    "Party Name": item.Name_Of_Party,
                    "Taxable Amount": 0,
                    "CGST": 0,
                    "SGST": 0,
                    "IGST": 0,
                    "Bill Amount": 0,
                    "TDS Amount": 0,
                };
            }

            partyTotals[party]["Taxable Amount"] += Number(item.Taxable_Amt) || 0;
            partyTotals[party]["CGST"] += Number(item.CGST) || 0;
            partyTotals[party]["SGST"] += Number(item.SGST) || 0;
            partyTotals[party]["IGST"] += Number(item.IGST) || 0;
            partyTotals[party]["Bill Amount"] += Number(item.Bill_Amount) || 0;
            partyTotals[party]["TDS Amount"] += Number(item.TDS_Amt) || 0;
        });

        const formattedData = Object.values(partyTotals).map(entry => ({
            PAN: entry.PAN,
            "Party Code": entry["Party Code"],
            "Party Name": entry["Party Name"],
            "Taxable Amount": entry["Taxable Amount"],
            "CGST": entry["CGST"],
            "SGST": entry["SGST"],
            "IGST": entry["IGST"],
            "Bill Amount": entry["Bill Amount"],
            "TDS Amount": entry["TDS Amount"]
        }));

        const ws = XLSX.utils.json_to_sheet(formattedData, { header: headers });

        // Step 4: Format columns
        const wsCols = [
            { wch: 15 },
            { wch: 10 },
            { wch: 30 },
            { wch: 15 },
            { wch: 10 },
            { wch: 10 },
            { wch: 10 },
            { wch: 15 },
            { wch: 12 },
        ];
        ws["!cols"] = wsCols;

        XLSX.utils.book_append_sheet(wb, ws, 'PurchaseTDSRegister');
        XLSX.writeFile(wb, 'PurchaseTDSRegister.xlsx');
    };


    const handlePrint = async () => {
        try {
            const companyName = Company_Name;
            const fromDate = searchParams.get('fromDate');
            const toDate = searchParams.get('toDate');

            if (!reportData || reportData.length === 0) {
                console.error("Error: reportData is empty or undefined");
                return;
            }

            const pdfBlob = await generatePDF(companyName, fromDate, toDate, reportData);

            if (!pdfBlob || !(pdfBlob instanceof Blob)) {
                console.error("Error: Invalid PDF Blob", pdfBlob);
                return;
            }

            const pdfUrl = URL.createObjectURL(pdfBlob);
            const win = window.open(pdfUrl);

            if (!win) {
                console.error("Popup blocked! Allow popups to print the PDF.");
                return;
            }

            setTimeout(() => win.print(), 1000);
        } catch (error) {
            console.error("Error in handlePrint:", error);
        }
    };


    const generatePDF = async (companyName, fromDate, toDate, reportData) => {
        if (!Array.isArray(reportData) || reportData.length === 0) {
            console.error("Error: reportData is invalid", reportData);
            return;
        }

        const doc = new jsPDF();
        const groupedData = groupReportData(reportData) || {};
        const tableData = [];

        doc.setFontSize(16);
        doc.text(companyName, doc.internal.pageSize.getWidth() / 2, 10, { align: 'center' });

        doc.setFontSize(10);
        doc.text(`Purchase TDS Register From: ${formatDate(fromDate)} To ${formatDate(toDate)}`, 10, 20);

        tableData.push(['Bill No', 'Customer Name', 'Taxable Amt', 'CGST Amt', 'SGST Amt', 'IGST Amt', 'Bill Amount', 'TDS']);

        const grandTotals1 = {
            TotalTaxable_Amt: 0,
            CGSTAmt: 0,
            SGSTAmt: 0,
            IGSTAmt: 0,
            BillamountAmt: 0,
            TDS_Amt: 0
        };

        Object.entries(groupedData).forEach(([key, totals]) => {
            if (!totals.TotalTaxable_Amt) return;

            const parts = key.split('-');
            const pan = parts[parts.length - 1];
            const PartyName = parts.slice(1, -1).join('-');
             const partyCode = parts[0];

            tableData.push([
                pan,
                partyCode,
                PartyName,
                formatReadableAmount(totals.TotalTaxable_Amt),
                formatReadableAmount(totals.CGSTAmt),
                formatReadableAmount(totals.SGSTAmt),
                formatReadableAmount(totals.IGSTAmt),
                formatReadableAmount(totals.BillamountAmt),
                formatReadableAmount(totals.TDSAmt),
            ]);

            grandTotals1.TotalTaxable_Amt += totals.TotalTaxable_Amt;
            grandTotals1.CGSTAmt += totals.CGSTAmt;
            grandTotals1.SGSTAmt += totals.SGSTAmt;
            grandTotals1.IGSTAmt += totals.IGSTAmt;
            grandTotals1.BillamountAmt += totals.BillamountAmt;
            grandTotals1.TDS_Amt += totals.TDS_Amt;
        });

        tableData.push([
            '', 'Total',
            formatReadableAmount(grandTotals.TotalTaxable_Amt),
            formatReadableAmount(grandTotals.CGSTAmt),
            formatReadableAmount(grandTotals.SGSTAmt),
            formatReadableAmount(grandTotals.IGSTAmt),
            formatReadableAmount(grandTotals.BillamountAmt),
            formatReadableAmount(grandTotals.TDSAmt)
        ]);

        doc.autoTable({
            headStyles: {
                fillColor: [255, 0, 0],
                fontStyle: 'bold',
            },
            body: tableData,
            margin: { top: 25 },
            styles: {
                fontSize: 6,
                cellPadding: 1,
                halign: 'center',
            },
            columnStyles: {
                0: { halign: 'left' },
                1: { halign: 'left' },
                2: { halign: 'right' },
                3: { halign: 'right' },
                4: { halign: 'right' },
                5: { halign: 'right' },
                6: { halign: 'right' },
                7: { halign: 'right' },
                8: { halign: 'right' },  
            },
            theme: 'grid',
        });

        return doc.output('blob');
    };

    const groupReportData = (data) => {
        const groupedData = {};
        data.forEach((item) => {
            const key = `${item.Party_Code}-${item.Name_Of_Party}-${item.Pan}`;
            if (!groupedData[key]) {
                groupedData[key] = {
                    items: [],
                    TotalTaxable_Amt: 0,
                    CGSTAmt: 0,
                    SGSTAmt: 0,
                    IGSTAmt: 0,
                    BillamountAmt: 0,
                    TDSAmt: 0,
                };
            }
            groupedData[key].items.push(item);
            groupedData[key].TotalTaxable_Amt += parseFloat(item.Taxable_Amt) || 0;
            groupedData[key].CGSTAmt += parseFloat(item.CGST) || 0;
            groupedData[key].SGSTAmt += parseFloat(item.SGST) || 0;
            groupedData[key].IGSTAmt += parseFloat(item.IGST) || 0;
            groupedData[key].BillamountAmt += parseFloat(item.Bill_Amount) || 0;
            groupedData[key].TDSAmt += parseFloat(item.TDS_Amt) || 0;

        });
        return groupedData;
    };

    const groupedReportData = groupReportData(reportData);

    grandTotals = useMemo(() => {
        return Object.values(groupedReportData).reduce(
            (totals, { TotalTaxable_Amt, CGSTAmt, SGSTAmt, IGSTAmt, BillamountAmt, TDSAmt }) => {
                totals.TotalTaxable_Amt += TotalTaxable_Amt || 0;
                totals.CGSTAmt += CGSTAmt || 0;
                totals.SGSTAmt += SGSTAmt || 0;
                totals.IGSTAmt += IGSTAmt || 0;
                totals.BillamountAmt += BillamountAmt || 0;
                totals.TDSAmt += TDSAmt || 0;
                return totals;
            },
            { TotalTaxable_Amt: 0, CGSTAmt: 0, SGSTAmt: 0, IGSTAmt: 0, BillamountAmt: 0, TDSAmt: 0 }
        );
    }, [groupedReportData]);

    return (
        <div style={{marginTop:"-80px"}}>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "24px", fontWeight: "bold" }}>{Company_Name}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px", textDecoration: 'underline', fontWeight: "550" }}>GSTN : {Company_GSTNO}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "20px", fontWeight: "bold" }}>Purchase TDS Register</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px" }}>{FormaDateBalanceSheet(fromDate)} to {FormaDateBalanceSheet(toDate)}</Typography>

            <div className="mb-3 row align-items-center">
                <div className="col-auto">
                    <button className="btn btn-secondary me-2" onClick={handlePrint}>Print</button>
                    <button className="btn btn-success" onClick={handleExportToExcel}>Export to Excel</button>
                </div>
            </div>

            <div style={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto', border: '1px solid #ccc' }}>
                <Table stickyHeader sx={{ minWidth: 700 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0, zIndex: 1 }}>
                            <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>PAN</TableCell>
                             <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>Party Code</TableCell>
                            <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>Party Name</TableCell>
                            <TableCell style={{ textAlign: "right", fontWeight: "bold" }}>Taxable Amount</TableCell>
                            <TableCell style={{ textAlign: "right", fontWeight: "bold" }}>CGST Amount</TableCell>
                            <TableCell style={{ textAlign: "right", fontWeight: "bold" }}>SGST Amount</TableCell>
                            <TableCell style={{ textAlign: "right", fontWeight: "bold" }}>IGST Amount</TableCell>
                            <TableCell style={{ textAlign: "right", fontWeight: "bold" }}>Bill Amount</TableCell>
                            <TableCell style={{ textAlign: "right", fontWeight: "bold" }}>TDS</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {Object.entries(groupedReportData).map(([key, { items, TotalTaxable_Amt, CGSTAmt, SGSTAmt, IGSTAmt, BillamountAmt, TDSAmt }]) => {
                            const parts = key.split('-');
                            const pan = parts[parts.length - 1];
                            const PartyName = parts.slice(1, -1).join('-');

                            return (
                                <React.Fragment key={key}>
                                    <TableRow>
                                        <TableCell>{pan}</TableCell>
                                        <TableCell>{parts[0]}</TableCell>
                                        <TableCell>{PartyName}</TableCell>
                                        <TableCell align="right">{formatReadableAmount(TotalTaxable_Amt.toFixed(2))}</TableCell>
                                        <TableCell align="right">{formatReadableAmount(CGSTAmt.toFixed(2))}</TableCell>
                                        <TableCell align="right">{formatReadableAmount(SGSTAmt.toFixed(2))}</TableCell>
                                        <TableCell align="right">{formatReadableAmount(IGSTAmt.toFixed(2))}</TableCell>
                                        <TableCell align="right">{formatReadableAmount(BillamountAmt.toFixed(2))}</TableCell>
                                        <TableCell align="right">{formatReadableAmount(TDSAmt.toFixed(2))}</TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>

                    <TableFooter>
                        <TableRow sx={{ position: 'sticky', bottom: 0, backgroundColor: 'yellow', zIndex: 1 }}>
                            <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>Grand Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatReadableAmount(grandTotals.TotalTaxable_Amt.toFixed(2))}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatReadableAmount(grandTotals.CGSTAmt.toFixed(2))}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatReadableAmount(grandTotals.SGSTAmt.toFixed(2))}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatReadableAmount(grandTotals.IGSTAmt.toFixed(2))}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatReadableAmount(grandTotals.BillamountAmt.toFixed(2))}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatReadableAmount(grandTotals.TDSAmt.toFixed(2))}</TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </div>

            {loading && (
                <div style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999
                }}>
                    <RingLoader size={80} />
                </div>
            )}
            {error && <div className="alert alert-danger">{error}</div>}
        </div>
    );
};

export default PurchaseTDSRegister;
